import * as THREE from 'three';

// ══════════════════════════════════════════════════════════════
//  SOLID PROCEDURAL BEYBLADE MODEL  v4 — TOTAL REDESIGN
//
//  Every single part is RADICALLY different in shape, detail,
//  silhouette, and decoration. No two parts share geometry.
// ══════════════════════════════════════════════════════════════

const V = (x, y) => new THREE.Vector2(x, y);
const TAU = Math.PI * 2;

const TIP_HEIGHT = {
  attack: 0.00, stamina: -0.08, defense: 0.02, balance: -0.02,
  jolt: 0.04, orbit: -0.06, destroy: 0.05, bearing: -0.10,
  xtreme: 0.01, volcanic: -0.04,
};

/* ═══ ENTRY ═══════════════════════════════════════════════ */
export function createBeybladeModel(config) {
  const group = new THREE.Group();
  const m = makeMats(new THREE.Color(config.color));
  const hOff = TIP_HEIGHT[config.tip] || 0;
  const bodyTop = 0.19 + hOff;

  // Solid body lathe
  const body = new THREE.LatheGeometry([
    ...tipCurve(config.tip),
    V(0.16, 0.00+hOff), V(0.24, 0.025+hOff), V(0.28, 0.05+hOff),
    V(0.32, 0.065+hOff), V(0.28, 0.07+hOff), V(0.55, 0.09+hOff),
    V(0.68, 0.11+hOff), V(0.735, 0.13+hOff), V(0.68, 0.155+hOff),
    V(0.54, 0.175+hOff), V(0.25, bodyTop), V(0.00, bodyTop),
  ], 64);
  group.add(new THREE.Mesh(body, m.main));

  // Core rim accent
  const rim = new THREE.LatheGeometry([
    V(0.54, 0.088+hOff), V(0.68, 0.108+hOff), V(0.74, 0.131+hOff),
    V(0.68, 0.157+hOff), V(0.54, 0.177+hOff),
  ], 64);
  group.add(new THREE.Mesh(rim, m.disc));

  layerBlades(config.layer, group, m, hOff);
  coreElements(config.core, group, m, hOff);
  tipDetail(config.tip, group, m);
  centerBolt(group, m, bodyTop);
  return group;
}

/* ═══ MATERIALS ═══════════════════════════════════════════ */
function makeMats(base) {
  const g = base.clone();
  return {
    main:     mat(base, 0.55, 0.22, g, 0.06),
    accent:   mat(base.clone().lerp(new THREE.Color(0xffffff), 0.35), 0.70, 0.12, g, 0.12),
    dark:     mat(base.clone().multiplyScalar(0.25), 0.60, 0.30),
    chrome:   mat(0xcccccc, 0.95, 0.04),
    dkChrome: mat(0x2a2a2e, 0.90, 0.10),
    rubber:   mat(0x222222, 0.05, 0.92),
    disc:     mat(base.clone().lerp(new THREE.Color(0xaaaaaa), 0.5), 0.92, 0.06),
    gem:      mat(g, 0.30, 0.08, g, 0.90, true),
    glow:     mat(0xff4400, 0.10, 0.60, new THREE.Color(0xff6600), 1.2),
    gold:     mat(0xd4a017, 0.85, 0.15, new THREE.Color(0xffcc00), 0.15),
    ice:      mat(0x88ccff, 0.30, 0.10, new THREE.Color(0x66aaff), 0.4, true),
  };
}
function mat(c, met, rough, em, emI, trans) {
  const o = { color: c, metalness: met, roughness: rough };
  if (em) { o.emissive = em; o.emissiveIntensity = emI || 0; }
  if (trans) { o.transparent = true; o.opacity = 0.85; }
  return new THREE.MeshStandardMaterial(o);
}


/* ═══════════════════════════════════════════════════════════
 *  TIP CURVES — completely different silhouettes
 * ═══════════════════════════════════════════════════════════ */
function tipCurve(t) {
  switch (t) {
    case 'attack':   // Wide flat rubber disc
      return [V(0,-.12),V(.14,-.12),V(.16,-.10),V(.16,-.05),V(.15,-.02),V(.14,-.01)];
    case 'stamina':  // Ultra-thin needle
      return [V(0,-.38),V(.003,-.36),V(.005,-.30),V(.010,-.22),V(.025,-.14),V(.06,-.07),V(.13,-.02)];
    case 'defense':  // Fat hemisphere
      return [V(0,-.30),V(.05,-.305),V(.12,-.28),V(.17,-.20),V(.19,-.12),V(.17,-.06),V(.14,-.02)];
    case 'balance':  // Smooth cone
      return [V(0,-.26),V(.015,-.25),V(.04,-.20),V(.07,-.15),V(.10,-.10),V(.13,-.05),V(.14,-.02)];
    case 'jolt':     // Stepped spring tiers
      return [V(0,-.16),V(.10,-.16),V(.10,-.12),V(.05,-.12),V(.05,-.08),V(.12,-.08),V(.12,-.04),V(.14,-.01)];
    case 'orbit':    // Pin with orbit ring groove
      return [V(0,-.32),V(.008,-.30),V(.018,-.24),V(.02,-.18),V(.09,-.16),V(.025,-.14),V(.05,-.08),V(.13,-.02)];
    case 'destroy':  // Massive ultra-wide
      return [V(0,-.09),V(.19,-.09),V(.21,-.07),V(.20,-.04),V(.17,-.02),V(.14,-.01)];
    case 'bearing':  // Ball bearing bulge + thin shaft
      return [V(0,-.36),V(.02,-.36),V(.03,-.34),V(.008,-.30),V(.010,-.22),V(.025,-.14),V(.07,-.06),V(.13,-.02)];
    case 'xtreme':   // Angular zigzag
      return [V(0,-.20),V(.13,-.20),V(.16,-.16),V(.07,-.12),V(.15,-.08),V(.08,-.04),V(.14,-.01)];
    case 'volcanic': // Double-bulge with neck
      return [V(0,-.30),V(.09,-.30),V(.11,-.26),V(.04,-.22),V(.035,-.18),V(.10,-.14),V(.13,-.08),V(.14,-.02)];
    default:
      return [V(0,-.22),V(.06,-.18),V(.10,-.10),V(.13,-.04),V(.14,-.02)];
  }
}


/* ═══════════════════════════════════════════════════════════
 *  TIP DETAILS — every tip has a unique visual identity
 * ═══════════════════════════════════════════════════════════ */
function tipDetail(t, g, m) {
  switch (t) {

    case 'attack': {
      // Flat rubber contact disc with cross-hatch grip pattern
      const pad = cyl(m.rubber, 0.15, 0.15, 0.014, 24);
      pad.position.y = -0.12; g.add(pad);
      // Cross-hatch ridges (2 perpendicular pairs)
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU;
        addBox(g, m.rubber, [0.12, 0.016, 0.018],
          [Math.cos(a)*0.01, -0.115, Math.sin(a)*0.01], [0, a, 0]);
      }
      // 8 perimeter studs
      for (let i = 0; i < 8; i++) {
        const a = (i/8)*TAU;
        const s = cyl(m.chrome, 0.008, 0.010, 0.012, 6);
        s.position.set(Math.cos(a)*0.12, -0.115, Math.sin(a)*0.12);
        g.add(s);
      }
      // Central cross mark
      addBox(g, m.dkChrome, [0.04, 0.005, 0.005], [0, -0.115, 0]);
      addBox(g, m.dkChrome, [0.005, 0.005, 0.04], [0, -0.115, 0]);
      break;
    }

    case 'stamina': {
      // Ultra-fine chrome needle with triple graduated rings
      addSphere(g, m.chrome, 0.004, [0, -0.38, 0]);
      // Thin shaft
      const shaft = cyl(m.chrome, 0.002, 0.002, 0.18, 8);
      shaft.position.y = -0.29; g.add(shaft);
      // 3 graduated rings at different radii and heights
      addTorus(g, m.chrome, 0.008, 0.0015, [0, -0.35, 0]);
      addTorus(g, m.dkChrome, 0.018, 0.002, [0, -0.28, 0]);
      addTorus(g, m.chrome, 0.04, 0.003, [0, -0.16, 0]);
      // Micro stabilizer fins (3 at 120°)
      for (let i = 0; i < 3; i++) {
        const a = (i/3)*TAU;
        addBox(g, m.chrome, [0.002, 0.04, 0.015],
          [Math.cos(a)*0.015, -0.22, Math.sin(a)*0.015], [0, a, 0]);
      }
      break;
    }

    case 'defense': {
      // Fat hemisphere with 5 rubber grip bands (equator wrap)
      for (let r = 0; r < 6; r++) {
        const rad = 0.17 - r*0.022;
        addTorus(g, r%2===0 ? m.rubber : m.chrome, rad, 0.006
          + (r===0?0.004:0), [0, -0.08 - r*0.04, 0]);
      }
      // 8 chrome bumps around widest point
      for (let i = 0; i < 8; i++) {
        const a = (i/8)*TAU;
        addSphere(g, m.chrome, 0.012,
          [Math.cos(a)*0.18, -0.14, Math.sin(a)*0.18]);
      }
      // Central grip boss
      addSphere(g, m.rubber, 0.03, [0, -0.30, 0]);
      // Equator accent ring
      addTorus(g, m.gold, 0.19, 0.003, [0, -0.14, 0]);
      break;
    }

    case 'balance': {
      // Chrome point + 4 stabilizer vanes + midway disc
      addSphere(g, m.chrome, 0.015, [0, -0.26, 0]);
      // 4 vanes (like satellite stabilizers)
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU;
        const sh = new THREE.Shape();
        sh.moveTo(0, 0); sh.lineTo(0.005, 0.06); sh.lineTo(0.04, 0.06);
        sh.lineTo(0.04, 0.045); sh.lineTo(0.01, 0); sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {depth:0.003, bevelEnabled:false});
        geo.rotateX(-Math.PI/2); geo.rotateY(a);
        const vane = new THREE.Mesh(geo, m.chrome);
        vane.position.set(Math.cos(a)*0.02, -0.22, Math.sin(a)*0.02);
        g.add(vane);
      }
      // Mid disc
      const disc = cyl(m.dkChrome, 0.08, 0.07, 0.008, 12);
      disc.position.y = -0.14; g.add(disc);
      addTorus(g, m.chrome, 0.08, 0.003, [0, -0.14, 0]);
      break;
    }

    case 'jolt': {
      // Visible spiral spring + wide impact cap at base
      // Spring coils (6 rings getting wider)
      for (let i = 0; i < 6; i++) {
        addTorus(g, m.chrome, 0.03 + i*0.012, 0.0035,
          [0, -0.15 + i*0.018, 0]);
      }
      // Impact mushroom cap
      const cap = cyl(m.chrome, 0.11, 0.09, 0.012, 16);
      cap.position.y = -0.16; g.add(cap);
      // V-notches around cap edge
      for (let i = 0; i < 8; i++) {
        const a = (i/8)*TAU;
        const notch = new THREE.Mesh(
          new THREE.ConeGeometry(0.008, 0.025, 3), m.dkChrome);
        notch.position.set(Math.cos(a)*0.09, -0.158, Math.sin(a)*0.09);
        notch.rotation.x = Math.PI; g.add(notch);
      }
      // Center peg
      const peg = cyl(m.dkChrome, 0.015, 0.02, 0.06, 6);
      peg.position.y = -0.13; g.add(peg);
      break;
    }

    case 'orbit': {
      // Thin pin with free-spinning orbital ring + ball bearings
      // Pin shaft
      const pin = cyl(m.chrome, 0.006, 0.006, 0.20, 8);
      pin.position.y = -0.22; g.add(pin);
      // LARGE orbit ring (the defining feature)
      addTorus(g, m.rubber, 0.08, 0.022, [0, -0.16, 0]);
      // Chrome inner race
      addTorus(g, m.chrome, 0.06, 0.006, [0, -0.16, 0]);
      // 12 ball bearings in orbit
      for (let i = 0; i < 12; i++) {
        const a = (i/12)*TAU;
        addSphere(g, m.chrome, 0.005,
          [Math.cos(a)*0.07, -0.16, Math.sin(a)*0.07]);
      }
      // Chrome point
      addSphere(g, m.chrome, 0.010, [0, -0.32, 0]);
      // Upper retention collar
      const col = cyl(m.dkChrome, 0.03, 0.025, 0.015, 8);
      col.position.y = -0.10; g.add(col);
      break;
    }

    case 'destroy': {
      // Massive flat rubber platform with 6-pointed star grip
      const plat = cyl(m.rubber, 0.19, 0.20, 0.016, 32);
      plat.position.y = -0.09; g.add(plat);
      // 6-pointed star ridges
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        addBox(g, m.rubber, [0.10, 0.018, 0.024],
          [Math.cos(a)*0.08, -0.085, Math.sin(a)*0.08], [0, a, 0]);
        // V-groove between star points
        const a2 = a + TAU/12;
        addBox(g, m.dkChrome, [0.06, 0.020, 0.008],
          [Math.cos(a2)*0.12, -0.085, Math.sin(a2)*0.12], [0, a2, 0]);
      }
      // 3 concentric rings
      addTorus(g, m.rubber, 0.16, 0.008, [0, -0.085, 0]);
      addTorus(g, m.dkChrome, 0.10, 0.005, [0, -0.088, 0]);
      addTorus(g, m.rubber, 0.04, 0.006, [0, -0.088, 0]);
      break;
    }

    case 'bearing': {
      // Precision ball bearing assembly — inner/outer race + balls
      // Outer race ring
      addTorus(g, m.dkChrome, 0.022, 0.010, [0, -0.35, 0]);
      // Precision ball at bottom
      addSphere(g, m.chrome, 0.018, [0, -0.36, 0]);
      // Inner race
      addTorus(g, m.chrome, 0.015, 0.005, [0, -0.35, 0]);
      // Individual bearing spheres (8 visible)
      for (let i = 0; i < 8; i++) {
        const a = (i/8)*TAU;
        addSphere(g, m.chrome, 0.003,
          [Math.cos(a)*0.018, -0.35, Math.sin(a)*0.018]);
      }
      // Long thin precision shaft
      const shaft = cyl(m.chrome, 0.004, 0.004, 0.18, 6);
      shaft.position.y = -0.26; g.add(shaft);
      // 2 retention rings
      addTorus(g, m.chrome, 0.025, 0.003, [0, -0.30, 0]);
      addTorus(g, m.dkChrome, 0.04, 0.004, [0, -0.18, 0]);
      // Chrome cage (3 vertical bars)
      for (let i = 0; i < 3; i++) {
        const a = (i/3)*TAU;
        addBox(g, m.chrome, [0.002, 0.12, 0.004],
          [Math.cos(a)*0.025, -0.28, Math.sin(a)*0.025], [0, a, 0]);
      }
      break;
    }

    case 'xtreme': {
      // Angular X-pattern rubber pads + chrome arrow guards
      // Main rubber X-blades (4 arms)
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU + Math.PI/4;
        // Wide rubber arm with angular profile
        const sh = new THREE.Shape();
        sh.moveTo(0, -0.015); sh.lineTo(0.12, -0.005);
        sh.lineTo(0.12, 0.005); sh.lineTo(0, 0.015); sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {depth:0.018, bevelEnabled:false});
        geo.rotateX(-Math.PI/2);
        const arm = new THREE.Mesh(geo, m.rubber);
        arm.position.set(0, -0.19, 0); arm.rotation.y = a;
        g.add(arm);
      }
      // 4 chrome arrow-head guards at 0°/90°/180°/270°
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU;
        const sh = new THREE.Shape();
        sh.moveTo(0, 0); sh.lineTo(0.03, 0.04); sh.lineTo(0.06, 0);
        sh.lineTo(0.03, -0.04); sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {depth:0.015, bevelEnabled:false});
        geo.rotateX(-Math.PI/2);
        const arrow = new THREE.Mesh(geo, m.chrome);
        arrow.position.set(Math.cos(a)*0.10, -0.185, Math.sin(a)*0.10);
        arrow.rotation.y = a; g.add(arrow);
      }
      // Center diamond
      const d = new THREE.Mesh(new THREE.OctahedronGeometry(0.014, 0), m.chrome);
      d.position.y = -0.19; d.rotation.y = Math.PI/4; g.add(d);
      break;
    }

    case 'volcanic': {
      // Double-sphere connected by exposed spring + heat vents
      // Lower contact sphere
      addSphere(g, m.rubber, 0.040, [0, -0.30, 0]);
      // Spring coils between spheres (visible in neck)
      for (let i = 0; i < 6; i++) {
        addTorus(g, m.chrome, 0.020 + i*0.006, 0.004,
          [0, -0.265 + i*0.020, 0]);
      }
      // Upper body sphere
      addSphere(g, m.rubber, 0.050, [0, -0.14, 0]);
      // Chrome endcaps
      addTorus(g, m.gold, 0.030, 0.005, [0, -0.29, 0]);
      addTorus(g, m.gold, 0.040, 0.005, [0, -0.15, 0]);
      // 6 heat vent slots (angled)
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        addBox(g, m.dkChrome, [0.003, 0.06, 0.012],
          [Math.cos(a)*0.05, -0.20, Math.sin(a)*0.05], [0, a, 0.25]);
      }
      // Glow accent at neck
      addTorus(g, m.glow, 0.030, 0.006, [0, -0.21, 0]);
      break;
    }
  }
}


/* ═══════════════════════════════════════════════════════════
 *  FORGE-CORE ELEMENTS — wildly different 3D architecture
 *  Each core uses a fundamentally different design language.
 * ═══════════════════════════════════════════════════════════ */
function coreElements(type, g, m, hOff) {
  const dY = 0.135 + (hOff || 0);

  switch (type) {

    case 'heavy': {
      // ═══ HEAVY: 6 massive L-shaped girder brackets ═══
      // Industrial steel I-beam construction. Heaviest possible.
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        const r = 0.60, x = Math.cos(a)*r, z = Math.sin(a)*r;
        // Main girder (L-shaped cross-section)
        const sh = new THREE.Shape();
        sh.moveTo(-0.04, -0.025); sh.lineTo(-0.04, 0.025);
        sh.lineTo(-0.02, 0.025); sh.lineTo(-0.02, 0.005);
        sh.lineTo(0.04, 0.005); sh.lineTo(0.04, -0.010);
        sh.lineTo(-0.02, -0.010); sh.lineTo(-0.02, -0.025);
        sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {depth:0.09, bevelEnabled:true, bevelThickness:0.004, bevelSize:0.004, bevelSegments:1});
        geo.rotateX(-Math.PI/2);
        const girder = new THREE.Mesh(geo, m.disc);
        girder.position.set(x, dY, z); girder.rotation.y = a;
        g.add(girder);
        // Hex bolt on each girder
        const bolt = cyl(m.chrome, 0.010, 0.010, 0.030, 6);
        bolt.position.set(x, dY+0.030, z); g.add(bolt);
        // Cross brace to next girder
        const a2 = a + TAU/12;
        addBox(g, m.dkChrome, [0.08, 0.012, 0.010],
          [Math.cos(a2)*0.55, dY+0.010, Math.sin(a2)*0.55], [0, a2, 0]);
      }
      // Massive outer containment ring
      addTorus(g, m.dkChrome, 0.65, 0.018, [0, dY, 0]);
      // Inner weight ring
      addTorus(g, m.disc, 0.46, 0.014, [0, dY+0.005, 0]);
      break;
    }

    case 'balanced': {
      // ═══ BALANCED: Gyroscope — 3 tilted rings + 4 counter-weights ═══
      // Precision engineering. 3 intersecting orbital rings.
      // Ring 1 — flat
      addTorus(g, m.disc, 0.62, 0.012, [0, dY, 0]);
      // Ring 2 — tilted 30°
      const r2 = new THREE.Mesh(new THREE.TorusGeometry(0.58, 0.010, 8, 32), m.disc);
      r2.position.set(0, dY, 0); r2.rotation.set(0.5, 0, 0); g.add(r2);
      // Ring 3 — tilted other way
      const r3 = new THREE.Mesh(new THREE.TorusGeometry(0.54, 0.010, 8, 32), m.disc);
      r3.position.set(0, dY, 0); r3.rotation.set(-0.3, 0, 0.5); g.add(r3);
      // 4 counter-weight pods where rings intersect
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU + Math.PI/8;
        const px = Math.cos(a)*0.60, pz = Math.sin(a)*0.60;
        // Pod body (capsule)
        const pod = cyl(m.chrome, 0.020, 0.025, 0.040, 8);
        pod.position.set(px, dY, pz); g.add(pod);
        // Pod cap sphere
        addSphere(g, m.chrome, 0.015, [px, dY+0.024, pz]);
      }
      // Central precision hub
      const hub = cyl(m.chrome, 0.05, 0.05, 0.025, 12);
      hub.position.y = dY; g.add(hub);
      addTorus(g, m.dkChrome, 0.05, 0.004, [0, dY+0.014, 0]);
      break;
    }

    case 'light': {
      // ═══ LIGHT: Spiderweb lattice — ultra-minimal wire frame ═══
      // Almost nothing there. Maximum negative space.
      // 6 ultra-thin spokes
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        addBox(g, m.disc, [0.003, 0.006, 0.26],
          [Math.cos(a)*0.46, dY, Math.sin(a)*0.46], [0, a, 0]);
      }
      // Outer wire ring (barely visible)
      addTorus(g, m.disc, 0.64, 0.003, [0, dY, 0]);
      // Mid wire ring
      addTorus(g, m.dkChrome, 0.48, 0.003, [0, dY+0.002, 0]);
      // Inner wire ring
      addTorus(g, m.chrome, 0.32, 0.003, [0, dY+0.004, 0]);
      // 6 tiny node dots where spokes hit outer ring
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        addSphere(g, m.chrome, 0.006,
          [Math.cos(a)*0.64, dY+0.004, Math.sin(a)*0.64]);
      }
      break;
    }

    case 'gravity': {
      // ═══ GRAVITY: 8 pendulum arms with weighted ends ═══
      // Like a centrifuge — weights fling outward during spin.
      for (let i = 0; i < 8; i++) {
        const a = (i/8)*TAU;
        // Thin arm
        addBox(g, m.dkChrome, [0.004, 0.008, 0.18],
          [Math.cos(a)*0.50, dY, Math.sin(a)*0.50], [0, a, 0]);
        // Weight at end (alternating sphere/cube for visual variety)
        const wx = Math.cos(a)*0.64, wz = Math.sin(a)*0.64;
        if (i % 2 === 0) {
          addSphere(g, m.disc, 0.032, [wx, dY+0.005, wz]);
        } else {
          addBox(g, m.disc, [0.04, 0.04, 0.04],
            [wx, dY+0.005, wz], [0, a+0.4, 0.2]);
        }
      }
      // Central dense mass cylinder
      const core = cyl(m.accent, 0.08, 0.08, 0.035, 16);
      core.position.y = dY; g.add(core);
      // Gyro ring (tilted)
      const gyro = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.005, 6, 20), m.chrome);
      gyro.position.set(0, dY+0.01, 0); gyro.rotation.set(0.6, 0, 0.4);
      g.add(gyro);
      break;
    }

    case 'forge': {
      // ═══ FORGE: 6 anvil blocks with chisel notches ═══
      // Blacksmith theme. Chunky irregular metal blocks.
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        const r = 0.58, x = Math.cos(a)*r, z = Math.sin(a)*r;
        // Anvil block (wide trapezoid)
        addBox(g, m.disc, [0.09, 0.045, 0.06],
          [x, dY+0.010, z], [0, a, 0]);
        // Anvil horn (cone on one side)
        const horn = new THREE.Mesh(new THREE.ConeGeometry(0.015, 0.06, 5), m.disc);
        horn.position.set(
          x + Math.cos(a+0.5)*0.05, dY+0.030,
          z + Math.sin(a+0.5)*0.05);
        horn.rotation.z = Math.PI/2; horn.rotation.y = a;
        g.add(horn);
        // Chrome striking plate on top
        addBox(g, m.chrome, [0.06, 0.008, 0.04],
          [x, dY+0.038, z], [0, a, 0]);
        // Chisel notch (dark slot)
        addBox(g, m.dkChrome, [0.03, 0.025, 0.005],
          [x, dY+0.005, z], [0, a+0.3, 0]);
      }
      // Forge ring with hammer marks
      addTorus(g, m.dkChrome, 0.50, 0.012, [0, dY, 0]);
      break;
    }

    case 'knuckle': {
      // ═══ KNUCKLE: 5 brass-knuckle fists with spiked ridges ═══
      // Maximum brutal impact. Big hemispherical dome bumps.
      for (let i = 0; i < 5; i++) {
        const a = (i/5)*TAU;
        const r = 0.60, x = Math.cos(a)*r, z = Math.sin(a)*r;
        // Large dome (half-sphere → MUCH bigger than before)
        const dome = new THREE.Mesh(
          new THREE.SphereGeometry(0.058, 12, 8, 0, TAU, 0, Math.PI/2), m.disc);
        dome.position.set(x, dY, z); g.add(dome);
        // Spike on top of each dome
        const spike = new THREE.Mesh(
          new THREE.ConeGeometry(0.012, 0.04, 5), m.chrome);
        spike.position.set(x, dY+0.065, z); g.add(spike);
        // Impact ring around dome base
        const ringR = 0.058;
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(ringR, 0.005, 6, 16), m.dkChrome);
        ring.rotation.x = Math.PI/2;
        ring.position.set(x, dY+0.005, z); g.add(ring);
        // 3 striations radiating outward
        for (let j = -1; j <= 1; j++) {
          const sa = a + j * 0.20;
          addBox(g, m.accent, [0.006, 0.010, 0.05],
            [Math.cos(sa)*0.59, dY+0.025, Math.sin(sa)*0.59], [0, sa, 0]);
        }
      }
      // Connecting guard band
      addTorus(g, m.disc, 0.50, 0.010, [0, dY, 0]);
      break;
    }

    case 'aero': {
      // ═══ AERO: 6 swept delta-wing airfoils + central turbine ═══
      // Jet-fighter sleekness. Dramatically swept shapes.
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU;
        const r = 0.57;
        // Delta wing shape (much more angular than before)
        const sh = new THREE.Shape();
        sh.moveTo(0, 0);
        sh.lineTo(0.02, 0.04);    // leading edge rises
        sh.lineTo(0.06, 0.045);   // peak
        sh.lineTo(0.14, 0.020);   // long taper
        sh.lineTo(0.18, 0.005);   // knife-edge trailing
        sh.lineTo(0.18, -0.005);
        sh.lineTo(0.14, -0.015);
        sh.lineTo(0.06, -0.030);
        sh.lineTo(0.02, -0.030);
        sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {
          depth: 0.035, bevelEnabled: true,
          bevelThickness: 0.004, bevelSize: 0.004, bevelSegments: 1
        });
        geo.rotateX(-Math.PI/2);
        const wing = new THREE.Mesh(geo, m.disc);
        wing.position.set(Math.cos(a)*r, dY+0.008, Math.sin(a)*r);
        wing.rotation.set(0.1, a+0.25, 0);
        g.add(wing);
        // Speed stripe
        addBox(g, m.accent, [0.12, 0.005, 0.003],
          [Math.cos(a+0.15)*0.60, dY+0.030, Math.sin(a+0.15)*0.60],
          [0, a+0.25, 0]);
      }
      // Central turbine intake (cone + rings)
      const turbine = new THREE.Mesh(
        new THREE.ConeGeometry(0.05, 0.05, 12), m.chrome);
      turbine.position.y = dY+0.020; g.add(turbine);
      addTorus(g, m.dkChrome, 0.05, 0.005, [0, dY+0.005, 0]);
      // Exhaust ring
      addTorus(g, m.accent, 0.08, 0.004, [0, dY, 0]);
      break;
    }

    case 'magma': {
      // ═══ MAGMA: Volcanic rock cluster with glowing lava veins ═══
      // Chaotic, organic, asymmetric. Nothing is regular.
      const chunks = [
        {a:0.0,  r:0.56, w:0.08, h:0.07, d:0.06},
        {a:0.8,  r:0.62, w:0.06, h:0.05, d:0.07},
        {a:1.5,  r:0.58, w:0.09, h:0.06, d:0.05},
        {a:2.2,  r:0.60, w:0.05, h:0.08, d:0.06},
        {a:2.9,  r:0.56, w:0.07, h:0.05, d:0.08},
        {a:3.7,  r:0.63, w:0.08, h:0.07, d:0.05},
        {a:4.4,  r:0.57, w:0.06, h:0.06, d:0.07},
        {a:5.2,  r:0.61, w:0.09, h:0.05, d:0.06},
        {a:5.8,  r:0.58, w:0.05, h:0.07, d:0.08},
      ];
      for (const c of chunks) {
        const x = Math.cos(c.a)*c.r, z = Math.sin(c.a)*c.r;
        // Rocky chunk (rotated oddly for organic feel)
        addBox(g, m.disc, [c.w, c.h, c.d],
          [x, dY + c.h*0.4, z], [c.a*0.1, c.a+0.3, c.a*0.07]);
        // Glowing lava vein on crack
        addBox(g, m.glow, [c.w*0.4, 0.005, c.d*0.3],
          [x, dY + c.h*0.85, z], [0, c.a+0.5, 0]);
        // Jagged peak on every other rock
        if (c.h > 0.06) {
          const peak = new THREE.Mesh(
            new THREE.ConeGeometry(c.w*0.3, c.h*0.5, 4), m.disc);
          peak.position.set(x, dY + c.h*1.0, z);
          peak.rotation.set(0.1, c.a, 0.12); g.add(peak);
        }
      }
      // Lava flow ring (glowing)
      addTorus(g, m.glow, 0.53, 0.010, [0, dY-0.005, 0]);
      break;
    }

    case 'vanguard': {
      // ═══ VANGUARD: 4 fortress tower shields + battlements ═══
      // Medieval castle wall. Crenellations on top.
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU;
        const r = 0.58, x = Math.cos(a)*r, z = Math.sin(a)*r;
        // Main tower shield (tall thick slab)
        const sh = new THREE.Shape();
        sh.moveTo(-0.10, -0.035);
        sh.lineTo(-0.12, 0.035);  // wider at top (shield shape)
        sh.lineTo( 0.12, 0.035);
        sh.lineTo( 0.10, -0.035);
        sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {
          depth: 0.07, bevelEnabled: true,
          bevelThickness: 0.006, bevelSize: 0.006, bevelSegments: 1
        });
        geo.rotateX(-Math.PI/2);
        const shield = new THREE.Mesh(geo, m.disc);
        shield.position.set(x, dY, z); shield.rotation.y = a;
        g.add(shield);
        // Battlements (3 crenellations on top)
        for (let j = -1; j <= 1; j++) {
          const bx = x + Math.cos(a)*0.01 + Math.cos(a+Math.PI/2)*j*0.06;
          const bz = z + Math.sin(a)*0.01 + Math.sin(a+Math.PI/2)*j*0.06;
          addBox(g, m.disc, [0.025, 0.025, 0.020],
            [bx, dY+0.055, bz], [0, a, 0]);
        }
        // Shield boss (center dome)
        addSphere(g, m.chrome, 0.022, [x, dY+0.042, z]);
        // Corner rivets
        for (let c = -1; c <= 1; c += 2) {
          for (let r2 = -1; r2 <= 1; r2 += 2) {
            const rx = x + Math.cos(a+Math.PI/2)*c*0.08 + Math.cos(a)*r2*0.008;
            const rz = z + Math.sin(a+Math.PI/2)*c*0.08 + Math.sin(a)*r2*0.008;
            addSphere(g, m.chrome, 0.005, [rx, dY+0.038, rz]);
          }
        }
        // Dark arrow slit
        addBox(g, m.dkChrome, [0.003, 0.030, 0.035],
          [x, dY+0.015, z], [0, a, 0]);
      }
      // Fortress wall ring
      addTorus(g, m.dkChrome, 0.52, 0.010, [0, dY, 0]);
      break;
    }

    case 'blitz': {
      // ═══ BLITZ: Circular sawblade ring with 12 razor teeth ═══
      // Aggressive cutting ring. Like a table-saw blade.
      // Outer teeth ring
      for (let i = 0; i < 12; i++) {
        const a = (i/12)*TAU;
        const r = 0.62;
        // Triangular tooth
        const sh = new THREE.Shape();
        sh.moveTo(0, -0.018); sh.lineTo(0.07, 0); sh.lineTo(0, 0.018);
        sh.closePath();
        const geo = new THREE.ExtrudeGeometry(sh, {depth:0.028, bevelEnabled:false});
        geo.rotateX(-Math.PI/2);
        const tooth = new THREE.Mesh(geo, m.disc);
        tooth.position.set(Math.cos(a)*r, dY, Math.sin(a)*r);
        tooth.rotation.y = a; g.add(tooth);
        // Chrome cutting edge
        addBox(g, m.chrome, [0.05, 0.005, 0.002],
          [Math.cos(a)*0.67, dY+0.015, Math.sin(a)*0.67], [0, a, 0]);
      }
      // Inner mechanism ring with lock tabs
      addTorus(g, m.dkChrome, 0.55, 0.015, [0, dY, 0]);
      // 6 lock tabs
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*TAU + Math.PI/12;
        addBox(g, m.chrome, [0.020, 0.020, 0.030],
          [Math.cos(a)*0.55, dY+0.008, Math.sin(a)*0.55], [0, a, 0]);
      }
      // Inner gear ring
      addTorus(g, m.accent, 0.45, 0.008, [0, dY+0.005, 0]);
      break;
    }

    default: {
      for (let i = 0; i < 4; i++) {
        const a = (i/4)*TAU;
        const c = cyl(m.disc, 0.025, 0.025, 0.03, 10);
        c.position.set(Math.cos(a)*0.62, dY, Math.sin(a)*0.62);
        g.add(c);
      }
    }
  }

  // Radial connecting struts
  const sn = type === 'light' ? 3 : 4;
  for (let i = 0; i < sn; i++) {
    const a = (i/sn)*TAU + 0.3;
    addBox(g, m.dkChrome, [0.014, 0.018, 0.17],
      [Math.cos(a)*0.44, dY-0.01, Math.sin(a)*0.44], [0, a, 0]);
  }
}


/* ═══════════════════════════════════════════════════════════
 *  CENTER BOLT
 * ═══════════════════════════════════════════════════════════ */
function centerBolt(g, m, bY) {
  const cap = cyl(m.accent, 0.075, 0.075, 0.045, 6);
  cap.position.y = bY + 0.023; g.add(cap);
  addTorus(g, m.chrome, 0.095, 0.012, [0, bY+0.023, 0]);
  const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.032, 0), m.gem);
  gem.position.y = bY + 0.06; gem.rotation.y = Math.PI/4; g.add(gem);
  for (let i = 0; i < 3; i++) {
    const a = (i/3)*TAU;
    addBox(g, m.dkChrome, [0.012, 0.025, 0.04],
      [Math.cos(a)*0.065, bY+0.03, Math.sin(a)*0.065], [0, a, 0]);
  }
}


/* ═══════════════════════════════════════════════════════════
 *  BLADE SYSTEM
 * ═══════════════════════════════════════════════════════════ */
const BH = 0.07;

function extBlade(shape, h, yOff) {
  h = h || BH;
  const BY = 0.175 + (yOff || 0);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: h, bevelEnabled: true,
    bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 2,
  });
  geo.rotateX(-Math.PI/2);
  geo.translate(0, BY, 0);
  return { geo, BY, h };
}

function layerBlades(type, g, m, hOff) {
  switch (type) {
    case 'striker':  bStriker(g, m, hOff); break;
    case 'vortex':   bVortex(g, m, hOff);  break;
    case 'dragon':   bDragon(g, m, hOff);  break;
    case 'phoenix':  bPhoenix(g, m, hOff); break;
    case 'valkyrie': bValkyrie(g, m, hOff);break;
    case 'achilles': bAchilles(g, m, hOff);break;
    case 'spriggan': bSpriggan(g, m, hOff);break;
    case 'longinus': bLonginus(g, m, hOff);break;
    case 'fafnir':   bFafnir(g, m, hOff);  break;
    case 'diabolos': bDiabolos(g, m, hOff);break;
  }
}


/* ═══════════════════════════════════════════════════════════
 *  ████  SMASH ATTACK LAYERS  ████
 *  4 layers. Each with a TOTALLY unique blade outline,
 *  number of blades, thickness, and decoration approach.
 * ═══════════════════════════════════════════════════════════ */

// ── STRIKER: 3 massive claw-hammer wings ──
// Wide triangular blade that ends in a heavy hammerhead smash face.
function bStriker(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.20, 0.0);
  sh.lineTo(0.24, 0.12);
  sh.lineTo(0.32, 0.18);       // WIDE base rise
  sh.quadraticCurveTo(0.50, 0.22, 0.64, 0.16);
  sh.lineTo(0.76, 0.08);       // begins descent
  sh.lineTo(0.84, -0.04);      // steep hammer slope
  sh.lineTo(0.90, -0.16);      // hammer shoulder
  sh.lineTo(0.96, -0.24);      // SMASH HEAD — massive downward
  sh.lineTo(0.98, -0.28);      // deepest point
  sh.lineTo(0.92, -0.30);      // flat impact face
  sh.lineTo(0.84, -0.24);
  sh.lineTo(0.76, -0.16);
  sh.quadraticCurveTo(0.58, -0.12, 0.40, -0.06);
  sh.lineTo(0.24, -0.02);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.10, hOff); // Extra thick

  for (let i = 0; i < 3; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/3)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = -0.14; // SMASH: outer edge angles DOWN for hammering impact
    sub.add(blade);
    // Chrome hammer face (HUGE block at impact point)
    addBox(sub, m.chrome, [0.12, 0.12, 0.16], [0.95, BY+0.02, 0.0]);
    // Impact crater
    addSphere(sub, m.dkChrome, 0.024, [0.95, BY+0.05, 0.0]);
    // Energy accent sweep
    addBox(sub, m.accent, [0.40, 0.022, 0.020],
      [0.52, BY+0.10+0.011, 0.14], [0, -0.12, 0]);
    // Underside reinforcement
    addBox(sub, m.dark, [0.34, 0.06, 0.018], [0.50, BY+0.015, -0.08]);
    g.add(sub);
  }
  fillPanels(g, m, 3, BY);
}


// ── VALKYRIE: 3 elongated katana sword-wings ──
// Extremely LONG and narrow, ending in a downward pommel tip. Very different from Striker.
function bValkyrie(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.20, 0.0);
  sh.lineTo(0.22, 0.06);
  sh.lineTo(0.30, 0.08);
  sh.lineTo(0.48, 0.10);        // long narrow body
  sh.lineTo(0.68, 0.09);
  sh.lineTo(0.84, 0.06);
  sh.lineTo(0.96, -0.02);       // sword edge curves down
  sh.lineTo(1.06, -0.10);       // very long reach
  sh.lineTo(1.12, -0.16);       // EXTREME tip — longest of any layer!
  sh.lineTo(1.10, -0.20);       // pommel end ball
  sh.lineTo(1.04, -0.16);
  sh.lineTo(0.94, -0.08);
  sh.lineTo(0.78, -0.06);
  sh.lineTo(0.56, -0.05);
  sh.lineTo(0.34, -0.03);
  sh.lineTo(0.22, -0.01);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.06, hOff); // Thin like a blade

  for (let i = 0; i < 3; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/3)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = -0.12; // SMASH: outer edge angles DOWN like descending sword strike
    sub.add(blade);
    // Sword edge accent (full length sharp line)
    addBox(sub, m.accent, [0.56, 0.014, 0.010],
      [0.64, BY+0.06+0.007, 0.06], [0, -0.04, 0]);
    // Chrome pommel sphere at tip
    addSphere(sub, m.chrome, 0.030, [1.11, BY+0.028, -0.03]);
    // Tsuba cross-guard at base
    addBox(sub, m.dark, [0.04, 0.06, 0.14], [0.26, BY+0.03, 0.0]);
    // Blood groove (fuller) along blade body
    addBox(sub, m.dkChrome, [0.36, 0.010, 0.006],
      [0.60, BY+0.06+0.005, -0.03]);
    g.add(sub);
  }
  fillPanels(g, m, 3, BY);
}


// ── SPRIGGAN: 3 double-headed symmetrical mace blades ──
// WIDE and flat, with impact lobes on BOTH sides. Completely different silhouette.
function bSpriggan(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.lineTo(0.26, 0.12);
  sh.lineTo(0.34, 0.18);       // wide body
  sh.lineTo(0.48, 0.22);       // first lobe UP
  sh.lineTo(0.58, 0.18);
  sh.lineTo(0.68, 0.22);       // second lobe UP
  sh.lineTo(0.78, 0.16);
  sh.lineTo(0.86, 0.04);
  sh.lineTo(0.90, -0.08);      // transition
  sh.lineTo(0.88, -0.18);      // smash contact
  sh.lineTo(0.82, -0.22);      // deep lobe DOWN
  sh.lineTo(0.72, -0.18);
  sh.lineTo(0.62, -0.22);      // second lobe DOWN (symmetrical)
  sh.lineTo(0.52, -0.16);
  sh.lineTo(0.42, -0.20);      // third lobe DOWN
  sh.lineTo(0.34, -0.12);
  sh.lineTo(0.24, -0.04);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.10, hOff);

  for (let i = 0; i < 3; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/3)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = -0.13; // SMASH: outer lobes angle DOWN for multi-point hammering
    sub.add(blade);
    // Chrome smash contact blocks (3 per blade — on each lobe)
    addBox(sub, m.chrome, [0.05, 0.08, 0.08], [0.86, BY+0.03, 0.0]);
    addBox(sub, m.chrome, [0.04, 0.06, 0.06], [0.68, BY+0.03, 0.16]);
    addBox(sub, m.chrome, [0.04, 0.06, 0.06], [0.48, BY+0.03, 0.18]);
    // Center spine
    addBox(sub, m.accent, [0.44, 0.020, 0.018], [0.56, BY+0.10+0.010, 0.0]);
    // Rib reinforcement
    addBox(sub, m.dark, [0.30, 0.06, 0.016], [0.56, BY+0.018, 0.10]);
    addBox(sub, m.dark, [0.30, 0.06, 0.016], [0.56, BY+0.018, -0.10]);
    g.add(sub);
  }
  fillPanels(g, m, 3, BY);
}


// ── LONGINUS: 3 spiral drill lances ──
// NARROW and POINTED, spiraling forward. Completely different from the wide Spriggan.
function bLonginus(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.lineTo(0.24, 0.06);
  sh.lineTo(0.32, 0.10);
  sh.quadraticCurveTo(0.46, 0.14, 0.60, 0.10);
  sh.quadraticCurveTo(0.72, 0.06, 0.82, -0.02);
  sh.lineTo(0.92, -0.12);       // drill descending
  sh.lineTo(1.00, -0.22);       // deep drill point
  sh.lineTo(1.06, -0.30);       // ABSOLUTE deepest — like a lance
  sh.lineTo(1.04, -0.32);       // sharp drill end
  sh.lineTo(0.98, -0.26);
  sh.lineTo(0.90, -0.16);
  sh.quadraticCurveTo(0.74, -0.14, 0.56, -0.08);
  sh.lineTo(0.36, -0.04);
  sh.lineTo(0.24, -0.01);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.065, hOff); // Narrow

  for (let i = 0; i < 3; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/3)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = -0.16; // SMASH: drill lance angles DOWN sharply for piercing impact
    sub.add(blade);
    // Chrome lance tip (extended cone)
    const lance = new THREE.Mesh(
      new THREE.ConeGeometry(0.022, 0.14, 6), m.chrome);
    lance.position.set(1.05, BY+0.025, -0.02);
    lance.rotation.z = -1.0; // angled down-forward
    sub.add(lance);
    // Spiral accent ridges (twist along blade)
    for (let r = 0; r < 7; r++) {
      const t = 0.30 + r*0.11;
      const sw = Math.sin(r*0.9)*0.05;
      addBox(sub, m.accent, [0.06, 0.014, 0.012],
        [t, BY+0.065+0.007, 0.07+sw], [0, -0.10*r, 0]);
    }
    // Dark drag keel
    addBox(sub, m.dark, [0.36, 0.04, 0.014], [0.58, BY+0.012, -0.05]);
    g.add(sub);
  }
  // Pointed wedge fillers
  for (let i = 0; i < 3; i++) {
    const a = (i/3)*TAU + Math.PI/3;
    const sh2 = new THREE.Shape();
    sh2.moveTo(0,0); sh2.lineTo(0.14, 0.06); sh2.lineTo(0.14, -0.06);
    const fGeo = new THREE.ExtrudeGeometry(sh2, {depth:0.025, bevelEnabled:false});
    fGeo.rotateX(-Math.PI/2); fGeo.translate(0, BY, 0);
    const f = new THREE.Mesh(fGeo, m.dark);
    f.position.set(Math.cos(a)*0.30, 0, Math.sin(a)*0.30);
    f.rotation.y = a; g.add(f);
  }
}


/* ═══════════════════════════════════════════════════════════
 *  ████  BARRAGE ATTACK LAYERS  ████
 *  Dense multi-blade arrays with textured contact surfaces.
 *  MORE blades = MORE hits per rotation.
 * ═══════════════════════════════════════════════════════════ */

// ── VORTEX: 7 short turbine blades with sawtooth edges ──
// MANY narrow blades arranged like a jet turbine.
function bVortex(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.24, 0.0);
  sh.quadraticCurveTo(0.28, 0.06, 0.38, 0.08);
  // Sawtooth contact edge
  sh.lineTo(0.44, 0.06);
  sh.lineTo(0.48, 0.10);        // tooth
  sh.lineTo(0.52, 0.04);
  sh.lineTo(0.56, 0.09);        // tooth
  sh.lineTo(0.60, 0.02);
  sh.lineTo(0.64, 0.07);        // tooth
  sh.lineTo(0.68, 0.00);
  sh.quadraticCurveTo(0.64, -0.06, 0.52, -0.08);
  sh.quadraticCurveTo(0.38, -0.06, 0.28, -0.02);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.055, hOff); // Short/thin

  for (let i = 0; i < 7; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/7)*TAU;
    sub.add(new THREE.Mesh(geo, m.main));
    // Chrome tooth tips
    for (let t = 0; t < 3; t++) {
      addSphere(sub, m.chrome, 0.010,
        [0.48 + t*0.08, BY+0.055+0.010, 0.04]);
    }
    // Rubber contact strip
    addBox(sub, m.rubber, [0.20, 0.035, 0.016],
      [0.50, BY+0.027, 0.06], [0, -0.08, 0]);
    g.add(sub);
  }
}


// ── PHOENIX: 6 feather blades with layered barrage profile ──
// Wider and more elegant than Vortex, with scalloped edges.
function bPhoenix(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.quadraticCurveTo(0.24, 0.10, 0.34, 0.14);
  // Scalloped feather lobes
  sh.quadraticCurveTo(0.42, 0.12, 0.48, 0.16);
  sh.quadraticCurveTo(0.54, 0.12, 0.58, 0.15);
  sh.quadraticCurveTo(0.64, 0.11, 0.68, 0.14);
  sh.quadraticCurveTo(0.74, 0.08, 0.76, 0.02);
  sh.quadraticCurveTo(0.72, -0.06, 0.60, -0.10);
  sh.quadraticCurveTo(0.44, -0.08, 0.30, -0.04);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.060, hOff);

  for (let i = 0; i < 6; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/6)*TAU;
    sub.add(new THREE.Mesh(geo, m.main));
    // 3 feather barrage bumps
    for (let t = 0; t < 3; t++) {
      addSphere(sub, m.chrome, 0.010,
        [0.48+t*0.10, BY+0.060+0.010, 0.10-t*0.04]);
    }
    // Feather vein accents
    addBox(sub, m.accent, [0.26, 0.012, 0.005],
      [0.48, BY+0.060+0.006, 0.05]);
    addBox(sub, m.accent, [0.22, 0.012, 0.005],
      [0.46, BY+0.060+0.006, -0.02]);
    // Gem center
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.010, 0), m.gem);
    gem.position.set(0.48, BY+0.060+0.014, 0.0);
    gem.rotation.y = Math.PI/4; sub.add(gem);
    // Rubber pad
    addBox(sub, m.rubber, [0.10, 0.030, 0.014],
      [0.56, BY+0.030, -0.06], [0, 0.10, 0]);
    g.add(sub);
  }
}


// ── FAFNIR: 5 wide rubber-coated spin-absorb blades ──
// Thick, round, with large rubber pads. Very different from the narrow Vortex.
function bFafnir(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.quadraticCurveTo(0.28, 0.10, 0.38, 0.14);
  // Wide round profile for maximum spin-absorption contact
  sh.quadraticCurveTo(0.52, 0.16, 0.64, 0.12);
  sh.quadraticCurveTo(0.74, 0.06, 0.78, -0.02);
  sh.quadraticCurveTo(0.76, -0.10, 0.66, -0.14);
  sh.quadraticCurveTo(0.50, -0.14, 0.36, -0.08);
  sh.quadraticCurveTo(0.26, -0.04, 0.22, -0.02);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.075, hOff); // Thicker than others

  for (let i = 0; i < 5; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/5)*TAU;
    sub.add(new THREE.Mesh(geo, m.main));
    // MASSIVE rubber absorption pads (3 per blade!)
    addBox(sub, m.rubber, [0.18, 0.050, 0.030],
      [0.52, BY+0.037, 0.08], [0, -0.16, 0]);
    addBox(sub, m.rubber, [0.14, 0.045, 0.025],
      [0.58, BY+0.037, -0.06], [0, 0.10, 0]);
    addBox(sub, m.rubber, [0.08, 0.040, 0.020],
      [0.40, BY+0.037, 0.10], [0, -0.25, 0]);
    // Barrage contact bumps
    for (let t = 0; t < 4; t++) {
      addSphere(sub, m.chrome, 0.008,
        [0.64 - t*0.10, BY+0.075+0.008, 0.06]);
    }
    // Spiral drain accent
    addBox(sub, m.accent, [0.28, 0.012, 0.008],
      [0.48, BY+0.075+0.006, 0.03], [0, -0.12, 0]);
    g.add(sub);
  }
}


/* ═══════════════════════════════════════════════════════════
 *  ████  UPPER ATTACK LAYERS  ████
 *  Blade tips curve dramatically UPWARD to scoop & launch.
 *  Each has a wildly different claw/ramp design.
 * ═══════════════════════════════════════════════════════════ */

// ── DRAGON: 4 triple-claw fangs with ascending peaks ──
// Jagged, spiky, beast-like with 3 escalating fang points.
function bDragon(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.lineTo(0.26, 0.06);
  sh.lineTo(0.34, 0.10);
  sh.lineTo(0.40, 0.06);
  sh.lineTo(0.46, 0.18);        // Fang 1 — small
  sh.lineTo(0.50, 0.10);
  sh.lineTo(0.56, 0.26);        // Fang 2 — medium
  sh.lineTo(0.62, 0.16);
  sh.lineTo(0.68, 0.34);        // Fang 3 — LARGE
  sh.lineTo(0.74, 0.40);        // TIP — highest point!!
  sh.lineTo(0.80, 0.32);
  sh.lineTo(0.84, 0.18);
  sh.lineTo(0.84, 0.04);
  sh.lineTo(0.80, -0.08);
  sh.lineTo(0.72, -0.18);       // Deep under-scoop
  sh.lineTo(0.60, -0.20);       // Scoop basin
  sh.lineTo(0.48, -0.14);
  sh.lineTo(0.36, -0.08);
  sh.lineTo(0.26, -0.02);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.080, hOff);

  for (let i = 0; i < 4; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/4)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = 0.18; // UPPER: outer edge angled UP — opponents ride up the ramp!
    sub.add(blade);
    // Chrome FANGS at each claw peak (pointing UP!)
    const f1 = new THREE.Mesh(new THREE.ConeGeometry(0.020, 0.12, 5), m.chrome);
    f1.position.set(0.73, BY+0.080*0.8+0.08, 0.06);
    f1.rotation.z = -0.35; sub.add(f1);
    const f2 = new THREE.Mesh(new THREE.ConeGeometry(0.016, 0.10, 5), m.chrome);
    f2.position.set(0.56, BY+0.080*0.8+0.05, 0.08);
    f2.rotation.z = -0.30; sub.add(f2);
    const f3 = new THREE.Mesh(new THREE.ConeGeometry(0.012, 0.07, 4), m.chrome);
    f3.position.set(0.46, BY+0.080*0.8+0.03, 0.10);
    f3.rotation.z = -0.25; sub.add(f3);
    // Scale armor ridges along spine
    for (let r = 0; r < 6; r++) {
      addBox(sub, m.accent, [0.04, 0.015, 0.010],
        [0.32+r*0.09, BY+0.080+0.008, 0.05-r*0.008]);
    }
    // Dragon eye gem
    const eye = new THREE.Mesh(new THREE.OctahedronGeometry(0.014, 0), m.gem);
    eye.position.set(0.38, BY+0.080+0.018, 0.0);
    eye.rotation.y = Math.PI/4; sub.add(eye);
    // Under-scoop dark ramp
    addBox(sub, m.dark, [0.26, 0.05, 0.012], [0.60, BY+0.012, -0.10]);
    g.add(sub);
  }
}


// ── ACHILLES: 4 wide armored shield-ramps ──
// Completely different from Dragon — wide, flat, armored. Shovel-scoop design.
function bAchilles(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.20, 0.0);
  sh.lineTo(0.24, 0.10);
  sh.lineTo(0.34, 0.18);
  sh.lineTo(0.48, 0.24);        // wide rising ramp
  sh.lineTo(0.62, 0.30);
  sh.lineTo(0.74, 0.36);        // dramatic upward sweep
  sh.lineTo(0.82, 0.42);        // HIGHEST POINT — massive scoop
  sh.lineTo(0.88, 0.38);
  sh.lineTo(0.92, 0.28);
  sh.lineTo(0.90, 0.14);
  sh.lineTo(0.86, 0.02);
  sh.lineTo(0.80, -0.10);
  sh.lineTo(0.70, -0.18);       // deep underside
  sh.lineTo(0.56, -0.22);       // maximum scoop depth
  sh.lineTo(0.42, -0.16);
  sh.lineTo(0.30, -0.08);
  sh.lineTo(0.22, -0.02);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.11, hOff); // MASSIVE thickness

  for (let i = 0; i < 4; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/4)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = 0.20; // UPPER: wide shield-ramp angled UP for maximum scoop launch
    sub.add(blade);
    // Heavy armor plate
    addBox(sub, m.accent, [0.28, 0.026, 0.22], [0.56, BY+0.11+0.013, 0.0]);
    // Upward shield rim (tilted!)
    addBox(sub, m.dark, [0.08, 0.10, 0.24],
      [0.84, BY+0.08, 0.0], [0, 0, 0.30]); // tilted for scoop
    // Chrome studs (6 per blade)
    for (let s = 0; s < 3; s++) {
      addSphere(sub, m.chrome, 0.016,
        [0.80, BY+0.11+0.016, 0.10-s*0.10]);
      addSphere(sub, m.chrome, 0.012,
        [0.60, BY+0.11+0.012, 0.12-s*0.12]);
    }
    // Cross-brace
    addBox(sub, m.dkChrome, [0.24, 0.020, 0.012], [0.54, BY+0.11+0.010, 0.0]);
    // Under-scoop ramp plate
    addBox(sub, m.dark, [0.24, 0.035, 0.16], [0.60, BY+0.012, 0.0]);
    g.add(sub);
  }
}


// ── DIABOLOS: 4 dragon-emperor dual-crest crown blades ──
// ZIGZAG rising crests like a dragon crown. Very different from Achilles' smooth scoop.
function bDiabolos(g, m, hOff) {
  const sh = new THREE.Shape();
  sh.moveTo(0.22, 0.0);
  sh.lineTo(0.26, 0.08);
  sh.lineTo(0.32, 0.06);
  sh.lineTo(0.38, 0.20);        // crest 1
  sh.lineTo(0.42, 0.12);
  sh.lineTo(0.48, 0.26);        // crest 2
  sh.lineTo(0.52, 0.16);
  sh.lineTo(0.58, 0.30);        // crest 3
  sh.lineTo(0.62, 0.20);
  sh.lineTo(0.68, 0.34);        // crest 4
  sh.lineTo(0.72, 0.24);
  sh.lineTo(0.78, 0.38);        // crest 5 — HIGHEST!!
  sh.lineTo(0.84, 0.30);
  sh.lineTo(0.88, 0.18);
  sh.lineTo(0.88, 0.06);
  sh.lineTo(0.84, -0.06);
  sh.lineTo(0.76, -0.16);
  sh.lineTo(0.64, -0.12);
  sh.lineTo(0.52, -0.18);       // deep zigzag scoop
  sh.lineTo(0.40, -0.12);
  sh.lineTo(0.30, -0.04);
  sh.closePath();
  const { geo, BY } = extBlade(sh, 0.090, hOff);

  for (let i = 0; i < 4; i++) {
    const sub = new THREE.Group();
    sub.rotation.y = (i/4)*TAU;
    const blade = new THREE.Mesh(geo, m.main);
    blade.rotation.z = 0.17; // UPPER: crown crests angled UP — zigzag ramps launch opponents
    sub.add(blade);
    // Dragon emperor eye gem
    const eye = new THREE.Mesh(new THREE.OctahedronGeometry(0.020, 0), m.gem);
    eye.position.set(0.56, BY+0.090+0.024, 0.0);
    eye.rotation.y = Math.PI/4; sub.add(eye);
    // Chrome crown fangs at each crest peak (5 ascending)
    const peaks = [[0.38,0.03],[0.48,0.05],[0.58,0.06],[0.68,0.08],[0.78,0.10]];
    for (const [px, sz] of peaks) {
      const cr = new THREE.Mesh(
        new THREE.ConeGeometry(0.008+sz*0.06, sz+0.02, 4), m.chrome);
      cr.position.set(px, BY+0.090*0.9+sz*0.5+0.01, 0.08);
      cr.rotation.z = -0.18; sub.add(cr);
    }
    // Armor scales along body
    addBox(sub, m.accent, [0.26, 0.020, 0.16], [0.56, BY+0.090+0.010, 0.0]);
    // Dark wing veins
    for (let r = 0; r < 5; r++) {
      addBox(sub, m.dark, [0.05, 0.012, 0.006],
        [0.36+r*0.10, BY+0.012, 0.10-r*0.02]);
    }
    g.add(sub);
  }
  // Crown filler cross
  for (let i = 0; i < 4; i++) {
    const a = (i/4)*TAU + Math.PI/4;
    addBox(g, m.dark, [0.12, 0.028, 0.06],
      [Math.cos(a)*0.32, BY+0.014, Math.sin(a)*0.32], [0, a, 0]);
  }
}


/* ═══════════════════════════════════════════════════════════
 *  HELPERS
 * ═══════════════════════════════════════════════════════════ */
function fillPanels(g, m, n, BY) {
  for (let i = 0; i < n; i++) {
    const a = (i/n)*TAU + Math.PI/n;
    addBox(g, m.dark, [0.14, 0.028, 0.07],
      [Math.cos(a)*0.34, BY+0.014, Math.sin(a)*0.34], [0, a, 0]);
  }
}

function cyl(mat, rT, rB, h, seg) {
  return new THREE.Mesh(new THREE.CylinderGeometry(rT, rB, h, seg||12), mat);
}

function addBox(parent, mat, size, pos, rot) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), mat);
  mesh.position.set(...pos);
  if (rot) mesh.rotation.set(...rot);
  parent.add(mesh);
}
function addSphere(parent, mat, r, pos) {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8), mat);
  mesh.position.set(...pos);
  parent.add(mesh);
}
function addTorus(parent, mat, r, tube, pos) {
  const mesh = new THREE.Mesh(new THREE.TorusGeometry(r, tube, 8, 24), mat);
  mesh.rotation.x = Math.PI/2;
  mesh.position.set(...pos);
  parent.add(mesh);
}
